/**
 * Layout.jsx
 *
 * Application shell using the AE Tracker design system.
 * Sidebar with brand row, nav sections, user card.
 * Main content area renders children.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Avatar from './Avatar.jsx';
import logoMain from '../assets/ae_logo_main.svg';
import logoMobile from '../assets/ae_logo_mobile.svg';
import logoIcon from '../assets/ae_icon.svg';
import { useAuth, ROLE_LABELS } from '../auth/AuthContext.jsx';
import { visibleArticles } from '../lib/knowledge/index.js';
import { usePinLock } from '../contexts/PinLockContext.jsx';
import { lockDurationOptionsFor, LOCK_NEVER } from '../lib/pinLockLogic.js';
import { useClinicApprovals } from '../contexts/ClinicApprovalsContext.jsx';
import { NotificationIcon } from '@phosphor-icons/react/dist/csr/Notification';
import { getSubSessionName, getSubSessionJobTitle, clearSubSession } from '../api/apiFetch.js';
import ChangePinDialog from './ChangePinDialog.jsx';
import { AssumeIdentityDialog, ImpersonationBanner } from './ImpersonationControl.jsx';
import BsIcon from './BsIcon.jsx';
import WhatsNewModal from './WhatsNewModal.jsx';
import { WHATS_NEW_KEY, slidesFor } from '../lib/whatsNew.js';

const SIDEBAR_COLLAPSED_KEY = 'ae-sidebar-collapsed';
// Privacy mode: frosts photo thumbnails (case-file attachment cards) so a case
// can be opened in a shared or sensitive setting without flashing the previews.
// Per device, persisted. Opening a photo still shows it in full — that's deliberate.
const PRIVACY_KEY = 'ae:privacyMode';
// What's new: opens once per sign-in (sessionStorage) until the user ticks
// "Don't show again" for this release (localStorage, keyed by WHATS_NEW_KEY so
// the next release shows again). Always reachable from the user menu.
const WHATSNEW_DISMISSED_KEY = 'ae:whatsNew:dismissed';
const WHATSNEW_SEEN_KEY = 'ae:whatsNew:seen';

// Compact label for the auto-lock duration shown on the collapsed menu row.
function shortLockLabel(ms) {
  if (!ms) return 'Never';
  const min = Math.round(ms / 60000);
  return (min >= 120 && min % 60 === 0) ? `${min / 60}h` : `${min}m`;
}

export default function Layout({ children }) {
  const { user, logout, canImpersonate, isImpersonating, exitImpersonation } = useAuth();
  const { count: approvalsCount } = useClinicApprovals();
  // For a clinic mailbox: the signed-in staff member's name (from the sub-session)
  // shown in place of the shared account name, with a "Switch user" action.
  const isClinicMailbox = user?.role === 'ClinicMailbox';
  // On a shared clinic device show the person's full name (first + surname) with
  // their job title beneath — it's the sub-identity, not the shared account.
  // Assuming a mailbox identity has no sub-session token, so the person being
  // viewed as comes from the impersonation target instead.
  const subIdentityName = isClinicMailbox ? (getSubSessionName() || user?.subIdentityName || null) : null;
  const subIdentityTitle = isClinicMailbox ? getSubSessionJobTitle() : null;
  // Lock: clear the sub-identity and return to the initial sign-in screen (the
  // "Who's signing in?" gate), landing on All Cases so the next person never
  // inherits the previous person's deep link or in-memory state.
  const lockSession = () => { clearSubSession(); window.location.assign('/cases'); };
  const pinLock = usePinLock();
  const location = useLocation();
  const navigate = useNavigate();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1'; }
    catch { return false; }
  });
  const [privacyOn, setPrivacyOn] = useState(() => {
    try { return localStorage.getItem(PRIVACY_KEY) === '1'; } catch { return false; }
  });
  const togglePrivacy = () => setPrivacyOn(prev => {
    const next = !prev;
    try { localStorage.setItem(PRIVACY_KEY, next ? '1' : '0'); } catch { /* per-session only */ }
    return next;
  });

  const [whatsNewOpen, setWhatsNewOpen] = useState(false);
  useEffect(() => {
    if (!user) return;
    try {
      if (localStorage.getItem(WHATSNEW_DISMISSED_KEY) === WHATS_NEW_KEY) return;
      if (sessionStorage.getItem(WHATSNEW_SEEN_KEY) === WHATS_NEW_KEY) return;
      if (!slidesFor(user).length) return;
      sessionStorage.setItem(WHATSNEW_SEEN_KEY, WHATS_NEW_KEY);
      setWhatsNewOpen(true);
    } catch { /* storage unavailable — stay closed */ }
  }, [user]);
  const closeWhatsNew = useCallback((persist) => {
    if (persist) { try { localStorage.setItem(WHATSNEW_DISMISSED_KEY, WHATS_NEW_KEY); } catch { /* ignore */ } }
    setWhatsNewOpen(false);
  }, []);
  const [menuOpen, setMenuOpen]       = useState(false);
  const [changePinOpen, setChangePinOpen] = useState(false);
  const [assumeOpen, setAssumeOpen]   = useState(false);
  const menuRef = useRef(null);

  // Auto-lock duration control (user setting; Supervisor + SuperAdmin).
  const [lockDurOpen, setLockDurOpen] = useState(false);
  const [lockSaving, setLockSaving]   = useState(false);
  const [lockErr, setLockErr]         = useState(null);
  const canSetLock  = !!pinLock?.canSetLockDuration;
  const canNever    = !!pinLock?.canDisableAutoLock;
  const lockOptions = canSetLock ? lockDurationOptionsFor(canNever) : [];
  const lockPrefMs  = pinLock?.lockPrefMs;                    // null | 0 | >0 | undefined
  // No preference yet → land on the role default (SuperAdmin: "Never").
  const currentLockMs = (lockPrefMs === null || lockPrefMs === undefined)
    ? (canNever ? LOCK_NEVER : (pinLock?.roleDefaultInactivityMs || 0))
    : lockPrefMs;
  const lockDurationLabel = shortLockLabel(currentLockMs);

  const handlePickLock = async (ms) => {
    if (ms === currentLockMs) { setLockDurOpen(false); return; }
    setLockSaving(true); setLockErr(null);
    try {
      await pinLock.setLockDuration(ms);
      setLockDurOpen(false);
    } catch (e) {
      setLockErr(e?.message || 'Could not save. Try again.');
    } finally {
      setLockSaving(false);
    }
  };

  // Collapse the submenu whenever the user menu closes.
  useEffect(() => {
    if (!menuOpen) { setLockDurOpen(false); setLockErr(null); }
  }, [menuOpen]);

  useEffect(() => {
    try { localStorage.setItem(SIDEBAR_COLLAPSED_KEY, sidebarCollapsed ? '1' : '0'); }
    catch {}
  }, [sidebarCollapsed]);

  useEffect(() => {
    if (!menuOpen) return;
    function onDocClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpen]);

  useEffect(() => {
    const ro = new IntersectionObserver(
      (entries) => entries.forEach(e => {
        if (e.isIntersecting) { e.target.classList.add('visible'); ro.unobserve(e.target); }
      }),
      { threshold: 0.04, rootMargin: '0px 0px -20px 0px' }
    );

    const observe = () =>
      document.querySelectorAll('.reveal:not(.visible)').forEach(el => ro.observe(el));

    observe();

    /* Watch for elements added after async data loads */
    const mo = new MutationObserver(observe);
    mo.observe(document.body, { subtree: true, childList: true });

    return () => { ro.disconnect(); mo.disconnect(); };
  }, [location.pathname]);

  // Close sidebar whenever the user navigates to a different page
  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  // Derive initials for avatar
  const initials = user?.name
    ? user.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
    : '?';

  // Helper to compute nav-item active class based on current path.
  // exactOnly = true: only highlight when path matches exactly (e.g. /dashboard).
  // Otherwise: also highlight when path is a prefix (e.g. /cases matches /cases/123).
  function navClass(path, exactOnly = false) {
    const isActive =
      location.pathname === path ||
      (!exactOnly && location.pathname.startsWith(path + '/'));
    return `nav-item${isActive ? ' active' : ''}`;
  }

  return (
    <div className={`app${sidebarCollapsed ? ' sidebar-collapsed' : ''}${privacyOn ? ' privacy' : ''}`}>
      {/* ── Mobile top bar (hidden on desktop via CSS) ── */}
      <div className="mobile-topbar">
        <button
          className="btn icon ghost"
          onClick={() => setSidebarOpen(true)}
          aria-label="Open menu"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 20, height: 20 }}>
            <line x1="3" y1="6" x2="21" y2="6"/>
            <line x1="3" y1="12" x2="21" y2="12"/>
            <line x1="3" y1="18" x2="21" y2="18"/>
          </svg>
        </button>
        <Link to="/dashboard" style={{ display: 'flex', textDecoration: 'none' }}>
          <img src={logoMobile} alt="AE Track" style={{ height: 28, width: 'auto' }} />
        </Link>
        <Avatar photoUrl={user?.photoUrl} initials={initials} size={32} style={{ fontSize: 12 }} />
      </div>

      {/* ───────── Sidebar ───────── */}
      <aside className={sidebarOpen ? 'open' : ''} aria-label="Main navigation">
        {/* Close button — shown only on mobile via CSS */}
        <button
          className="btn icon ghost sidebar-close-btn"
          onClick={() => setSidebarOpen(false)}
          aria-label="Close menu"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 18, height: 18 }}>
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>

        {/* Brand row */}
        <Link to="/dashboard" className="brand-row" style={{ textDecoration: 'none' }}>
          {sidebarCollapsed ? (
            <img src={logoIcon} alt="AE Track" className="brand-mark-collapsed" />
          ) : (
            <img src={logoMain} alt="AE Track" style={{ width: '75%', height: 'auto', display: 'block' }} />
          )}
        </Link>

        {/* Collapse toggle — desktop only via CSS */}
        <button
          type="button"
          className="sidebar-collapse-btn"
          onClick={() => setSidebarCollapsed(c => !c)}
          aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14 }}>
            {sidebarCollapsed
              ? <polyline points="9 18 15 12 9 6"/>
              : <polyline points="15 18 9 12 15 6"/>}
          </svg>
        </button>

        {/* Overview section */}
        <div>
          <div className="nav-section-label">Overview</div>
          <div className="nav">
            {/* Dashboard — analytics/stats page */}
            {user?.canSeeDashboard && (
            <Link to="/dashboard" className={navClass('/dashboard', true)} data-label="Dashboard">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7" rx="1"/>
                <rect x="14" y="3" width="7" height="7" rx="1"/>
                <rect x="3" y="14" width="7" height="7" rx="1"/>
                <rect x="14" y="14" width="7" height="7" rx="1"/>
              </svg>
              <span className="nav-label">Dashboard</span>
            </Link>
            )}
            {/* All Cases — full list view */}
            <Link to="/cases" className={navClass('/cases')} data-label="All Cases">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
              </svg>
              <span className="nav-label">All Cases</span>
            </Link>
            {/* Knowledge — in-app user guide, per-role toggle + articles filtered
                per tier. Hidden when toggled off or no article matches the tier. */}
            {user?.canSeeKnowledge && visibleArticles(user).length > 0 && (
              <Link to="/knowledge" className={navClass('/knowledge')} data-label="Knowledge">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                </svg>
                <span className="nav-label">Knowledge</span>
              </Link>
            )}
            {/* Notifications — clinic-access approvals inbox (Supervisor+). The
                red dot marks un-actioned requests. */}
            {user?.canManageClinicAccess && (
              <Link to="/notifications" className={navClass('/notifications')} data-label="Notifications">
                <span style={{ position: 'relative', display: 'inline-flex' }}>
                  <NotificationIcon size={16} weight="bold" />
                  {approvalsCount > 0 && (
                    <span aria-hidden="true" style={{
                      position: 'absolute', top: -2, right: -2, width: 9, height: 9,
                      borderRadius: '50%', background: '#DC2626',
                      border: '2px solid var(--surface)',
                    }} />
                  )}
                </span>
                <span className="nav-label">
                  Notifications
                  {approvalsCount > 0 && (
                    <span style={{
                      marginLeft: 8, fontSize: 11, fontWeight: 700, color: '#fff',
                      background: '#DC2626', borderRadius: 10, padding: '1px 7px',
                    }}>{approvalsCount}</span>
                  )}
                </span>
              </Link>
            )}
          </div>
        </div>

        {/* Workspace section — hidden for any role that cannot submit cases */}
        {user?.canSubmitCase && (
          <div>
            {/* Collapsed-only divider — the section label that normally separates
                "view" actions from the "create" action is hidden when collapsed. */}
            <div className="nav-sep" aria-hidden="true" />
            <div className="nav-section-label">Workspace</div>
            <div className="nav">
              <Link to="/new-case" className={navClass('/new-case')} data-label="New Submission">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 5v14M5 12h14"/>
                </svg>
                <span className="nav-label">New Submission</span>
              </Link>
            </div>
          </div>
        )}

        {/* Admin Panel (and Ops Monitor for SuperAdmin) live in the user menu (bottom-left). The UI Book keeps its /ui-book route but is no longer listed. */}

        {/* User card */}
        <div className="user-card" ref={menuRef} style={{ position: 'relative' }}>
          <button
            type="button"
            onClick={() => setMenuOpen(o => !o)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            style={{
              display: 'flex', alignItems: 'center', gap: 10, width: '100%',
              padding: 0, background: 'transparent', border: 'none', cursor: 'pointer',
              textAlign: 'left',
            }}
          >
            <Avatar photoUrl={user?.photoUrl} initials={initials} />
            <div className="user-meta" style={{ flex: 1, minWidth: 0 }}>
              <div className="name">{subIdentityName || user?.name || 'Unknown'}</div>
              <div className="role">{subIdentityName ? (subIdentityTitle || 'Clinic staff') : (user?.roleLabel || ROLE_LABELS[user?.role] || user?.role || 'Unknown role')}</div>
            </div>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14, color: 'var(--text-3)', flexShrink: 0 }}>
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </button>

          {menuOpen && (
            <div role="menu" className="user-menu dd-shell dd-up">
              {/* Admin Panel — full for SuperAdmin, scoped tabs for Supervisor */}
              {user?.adminTabs && (
                <button
                  role="menuitem"
                  onClick={() => { setMenuOpen(false); navigate('/admin'); }}
                  className="dd-item"
                >
                  <BsIcon name="gear" />
                  Admin Panel
                </button>
              )}
              {user?.canManageAdminConfig && (
                <>
                  {user?.isSuperAdmin && (
                    <button
                      role="menuitem"
                      onClick={() => { setMenuOpen(false); navigate('/monitor'); }}
                      className="dd-item"
                    >
                      <BsIcon name="clipboard-data" />
                      Ops Monitor
                    </button>
                  )}
                  <div className="dd-sep" />
                </>
              )}
              {canImpersonate && (
                <>
                  <button
                    role="menuitem"
                    onClick={() => { setMenuOpen(false); setAssumeOpen(true); }}
                    className="dd-item"
                  >
                    <BsIcon name="person-bounding-box" />
                    {isImpersonating ? 'Switch identity…' : 'Assume identity…'}
                  </button>
                  {isImpersonating && (
                    <button
                      role="menuitem"
                      onClick={() => { setMenuOpen(false); exitImpersonation(); window.location.assign('/cases'); }}
                      className="dd-item"
                    >
                      <BsIcon name="x-lg" />
                      Exit impersonation
                    </button>
                  )}
                  <div className="dd-sep" />
                </>
              )}
              {slidesFor(user).length > 0 && (
                <>
                  <button
                    role="menuitem"
                    onClick={() => { setMenuOpen(false); setWhatsNewOpen(true); }}
                    className="dd-item"
                  >
                    <BsIcon name="gift" />
                    What’s new
                  </button>
                  <div className="dd-sep" />
                </>
              )}
              {pinLock?.enabled && pinLock?.required && (
                <>
                  <button
                    role="menuitem"
                    onClick={() => { setMenuOpen(false); pinLock.lock(); }}
                    className="dd-item"
                  >
                    <BsIcon name="lock" />
                    Lock now
                  </button>
                  <button
                    role="menuitem"
                    onClick={() => { setMenuOpen(false); setChangePinOpen(true); }}
                    className="dd-item"
                  >
                    <BsIcon name="pencil-square" />
                    Change PIN
                  </button>
                  {canSetLock && (
                    <>
                      <button
                        role="menuitem"
                        aria-haspopup="true"
                        aria-expanded={lockDurOpen}
                        onClick={() => setLockDurOpen(o => !o)}
                        className="dd-item"
                        title="How long before the session locks on inactivity."
                      >
                        <BsIcon name="clock" />
                        Auto-lock
                        <span className={`menu-value${lockDurOpen ? ' open' : ''}`}>
                          {lockDurationLabel}
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                        </span>
                      </button>
                      {lockDurOpen && (
                        <div role="group" aria-label="Lock after inactivity" className="dd-subgroup">
                          {lockOptions.map(opt => {
                            const selected = opt.ms === currentLockMs;
                            return (
                              <button
                                key={opt.ms}
                                role="menuitemradio"
                                aria-checked={selected}
                                disabled={lockSaving}
                                onClick={() => handlePickLock(opt.ms)}
                                className={`dd-item dd-subitem${selected ? ' selected' : ''}`}
                              >
                                {opt.label}
                              </button>
                            );
                          })}
                          {lockErr && <div className="dd-suberr" role="alert">{lockErr}</div>}
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
              <button
                role="menuitemcheckbox"
                aria-checked={privacyOn}
                onClick={togglePrivacy}
                className="dd-item"
                title={privacyOn ? 'Privacy on — photo previews are frosted. Click to show them.' : 'Privacy off — click to frost photo previews in case files.'}
              >
                <BsIcon name={privacyOn ? 'eye-slash' : 'eye'} />
                Privacy
                <span className={`menu-switch${privacyOn ? ' on' : ''}`} aria-hidden="true"><span className="knob" /></span>
              </button>
              <div className="dd-sep" />
              {isClinicMailbox && subIdentityName && !isImpersonating && (
                <button role="menuitem" onClick={() => { setMenuOpen(false); lockSession(); }} className="dd-item">
                  <BsIcon name="lock" />
                  Lock
                </button>
              )}
              <button role="menuitem" onClick={() => { setMenuOpen(false); logout(); }} className="dd-item">
                <BsIcon name="box-arrow-right" />
                Sign out
              </button>
            </div>
          )}
        </div>
      </aside>

      <WhatsNewModal
        open={whatsNewOpen}
        user={user}
        onClose={closeWhatsNew}
        onOpenArticle={(id) => { setWhatsNewOpen(false); navigate(`/knowledge/${id}`); }}
      />

      {/* Sidebar backdrop (mobile) */}
      {sidebarOpen && (
        <div
          className="sidebar-backdrop"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* ───────── Main ───────── */}
      <main id="main-content">
        <ImpersonationBanner />
        {children}
      </main>

      {changePinOpen && (
        <ChangePinDialog onClose={() => setChangePinOpen(false)} />
      )}

      {assumeOpen && (
        <AssumeIdentityDialog onClose={() => setAssumeOpen(false)} />
      )}
    </div>
  );
}

